import { TrendingUp, Clock, AlertCircle } from 'lucide-react'
import { useMemo } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  useCompletionTrend,
  formatPercent,
  type AnalyticsFilters,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface CompletionTrendChartProps {
  filters?: AnalyticsFilters
  days?: number
  className?: string
}

/**
 * 完成率趋势图表组件
 * Requirements: 3.7
 */
export function CompletionTrendChart({
  filters = {},
  days = 30,
  className,
}: CompletionTrendChartProps) {
  const { data: trend, isLoading, error } = useCompletionTrend(filters, days)

  // 计算图表数据
  const chartData = useMemo(() => {
    if (!trend || trend.length === 0) {return null}

    const maxRate = Math.max(...trend.map((t) => t.completionRate), 0.1)
    const minRate = Math.min(...trend.map((t) => t.completionRate), 0)

    return {
      points: trend.map((t, i) => ({
        ...t,
        x: (i / (trend.length - 1 || 1)) * 100,
        y: ((maxRate - t.completionRate) / (maxRate - minRate || 1)) * 100,
      })),
      maxRate,
      minRate,
    }
  }, [trend])

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            完成率趋势
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Clock className="h-5 w-5 animate-spin mr-2" />
            加载中...
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            完成率趋势
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-destructive">
            <AlertCircle className="h-5 w-5 mr-2" />
            加载失败
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!chartData || !trend || trend.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            完成率趋势
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            暂无数据
          </div>
        </CardContent>
      </Card>
    )
  }

  // 生成 SVG 路径
  const pathD = chartData.points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')

  // 生成填充区域路径
  const areaD = `${pathD} L 100 100 L 0 100 Z`

  // 计算趋势
  const firstRate = trend[0].completionRate
  const lastRate = trend[trend.length - 1].completionRate
  const trendDirection = lastRate > firstRate ? 'up' : lastRate < firstRate ? 'down' : 'stable'

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            完成率趋势
          </div>
          <span className="text-sm font-normal text-muted-foreground">
            最近 {days} 天
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* 统计摘要 */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center">
            <div className="text-2xl font-bold">{formatPercent(lastRate)}</div>
            <div className="text-xs text-muted-foreground">当前完成率</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">
              {trend.reduce((sum, t) => sum + t.completedCount, 0)}
            </div>
            <div className="text-xs text-muted-foreground">已完成任务</div>
          </div>
          <div className="text-center">
            <div
              className={cn(
                'text-2xl font-bold',
                trendDirection === 'up'
                  ? 'text-green-600'
                  : trendDirection === 'down'
                    ? 'text-red-600'
                    : ''
              )}
            >
              {trendDirection === 'up' ? '↑' : trendDirection === 'down' ? '↓' : '→'}
              {Math.abs((lastRate - firstRate) * 100).toFixed(1)}%
            </div>
            <div className="text-xs text-muted-foreground">趋势变化</div>
          </div>
        </div>

        {/* 简易折线图 */}
        <div className="relative h-40">
          {/* Y 轴标签 */}
          <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-muted-foreground">
            <span>{formatPercent(chartData.maxRate)}</span>
            <span>{formatPercent((chartData.maxRate + chartData.minRate) / 2)}</span>
            <span>{formatPercent(chartData.minRate)}</span>
          </div>

          {/* 图表区域 */}
          <div className="ml-14 h-full">
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="w-full h-full"
            >
              {/* 网格线 */}
              <line
                x1="0"
                y1="50"
                x2="100"
                y2="50"
                stroke="currentColor"
                strokeOpacity="0.1"
                strokeDasharray="2"
              />

              {/* 填充区域 */}
              <path d={areaD} fill="currentColor" fillOpacity="0.1" className="text-primary" />

              {/* 折线 */}
              <path
                d={pathD}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-primary"
              />

              {/* 数据点 */}
              {chartData.points.map((p, i) => (
                <circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r="2"
                  fill="currentColor"
                  className="text-primary"
                />
              ))}
            </svg>
          </div>
        </div>

        {/* X 轴标签 */}
        <div className="ml-14 flex justify-between text-xs text-muted-foreground mt-2">
          <span>{formatDate(trend[0].date)}</span>
          <span>{formatDate(trend[Math.floor(trend.length / 2)]?.date)}</span>
          <span>{formatDate(trend[trend.length - 1].date)}</span>
        </div>
      </CardContent>
    </Card>
  )
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) {return ''}
  const date = new Date(dateStr)
  return `${date.getMonth() + 1}/${date.getDate()}`
}
